import React, { useCallback, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import { backend_url, server } from "../../server";
import ImageCropperModal from '../Image/ImageCropperModal';
import { Rnd } from 'react-rnd';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const HEIC_EXTENSION_PATTERN = /\.(heic|heif)$/i;
const MAX_MEME_UPLOAD_BYTES = 15 * 1024 * 1024;
const MAX_MEME_IMAGE_DIMENSION = 1600;
const MEME_EDITOR_MAX_DIMENSION = 2400;
const MEME_IMAGE_QUALITY = 0.88;
const MEME_EDITOR_IMAGE_QUALITY = 0.9;
const IMAGE_PREP_TIMEOUT_MS = 30000;
const MEME_IMAGE_TOO_LARGE_MESSAGE = 'Image is too large. Please choose an image 15 MB or less.';
const MIN_MEME_TEXT_BOX_WIDTH = 80;
const MIN_MEME_TEXT_BOX_HEIGHT = 40;

const createUserVisibleError = (message) => {
  const error = new Error(message);
  error.isUserVisible = true;
  return error;
};

const clampNumber = (value, min, max) => Math.min(Math.max(value, min), max);

const clampDimension = (value, min, max) => {
  if (!max || max <= 0) {
    return value;
  }

  return clampNumber(value, Math.min(min, max), max);
};

const isHeicFile = (file) => {
  const fileType = file?.type?.toLowerCase() || '';
  const fileName = file?.name || '';

  return fileType === 'image/heic' || fileType === 'image/heif' || HEIC_EXTENSION_PATTERN.test(fileName);
};

const getJpegFileName = (fileName = 'meme-image') => {
  const nameWithoutHeicExtension = fileName.replace(HEIC_EXTENSION_PATTERN, '');
  return `${nameWithoutHeicExtension || 'meme-image'}.jpg`;
};

const withTimeout = (promise, timeoutMs, message) => new Promise((resolve, reject) => {
  const timeoutId = setTimeout(() => {
    reject(createUserVisibleError(message));
  }, timeoutMs);

  promise
    .then((value) => {
      clearTimeout(timeoutId);
      resolve(value);
    })
    .catch((error) => {
      clearTimeout(timeoutId);
      reject(error);
    });
});

const convertHeicToJpeg = async (file) => {
  const heic2anyModule = await withTimeout(
    import('heic2any'),
    IMAGE_PREP_TIMEOUT_MS,
    'HEIC conversion could not start. Please choose another image.'
  );
  const heic2any = heic2anyModule.default || heic2anyModule;
  const converted = await withTimeout(
    heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.92,
    }),
    IMAGE_PREP_TIMEOUT_MS,
    'HEIC conversion took too long. Please choose another image.'
  );
  const convertedBlob = Array.isArray(converted) ? converted[0] : converted;

  if (!convertedBlob) {
    throw createUserVisibleError('HEIC conversion failed. Please choose another image.');
  }

  return new File([convertedBlob], getJpegFileName(file.name), {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
};

const getMemeErrorMessage = (error) => {
  const responseData = error?.response?.data;

  if (Array.isArray(responseData?.errors) && responseData.errors.length > 0) {
    return responseData.errors.map((item) => item.message).filter(Boolean).join(' ');
  }

  if (responseData?.message) {
    return responseData.message;
  }

  if (responseData?.error) {
    return responseData.error;
  }

  if (error?.message === 'Network Error') {
    return 'Upload failed before the server responded. Please check your connection and try again.';
  }

  if (error?.isUserVisible && error.message) {
    return error.message;
  }

  return 'Failed to create meme. Please try again.';
};

const getImageDimensions = (image, maxDimension = MAX_MEME_IMAGE_DIMENSION) => {
  const width = image.naturalWidth || image.width || 1;
  const height = image.naturalHeight || image.height || 1;
  const scale = Math.min(1, maxDimension / Math.max(width, height));

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

const loadImageFromFile = (file) => new Promise((resolve, reject) => {
  const imageUrl = URL.createObjectURL(file);
  const image = new Image();
  const timeoutId = setTimeout(() => {
    URL.revokeObjectURL(imageUrl);
    reject(createUserVisibleError('Image preparation took too long. Please choose another image.'));
  }, IMAGE_PREP_TIMEOUT_MS);

  image.onload = () => {
    clearTimeout(timeoutId);
    URL.revokeObjectURL(imageUrl);
    resolve(image);
  };

  image.onerror = () => {
    clearTimeout(timeoutId);
    URL.revokeObjectURL(imageUrl);
    reject(createUserVisibleError('Unable to read selected image. Please choose another image.'));
  };

  image.src = imageUrl;
});

const canvasToBlob = (canvas, type, quality) => new Promise((resolve, reject) => {
  const timeoutId = setTimeout(() => {
    reject(createUserVisibleError('Image optimization took too long. Please choose another image.'));
  }, IMAGE_PREP_TIMEOUT_MS);

  canvas.toBlob((blob) => {
    clearTimeout(timeoutId);

    if (blob) {
      resolve(blob);
      return;
    }

    reject(createUserVisibleError('Unable to prepare image for upload. Please choose another image.'));
  }, type, quality);
});

const optimizeMemeImageFile = async (
  file,
  {
    maxDimension = MAX_MEME_IMAGE_DIMENSION,
    quality = MEME_IMAGE_QUALITY,
    maxBytes = MAX_MEME_UPLOAD_BYTES,
  } = {}
) => {
  if (!file || typeof window === 'undefined' || typeof document === 'undefined') {
    return file;
  }

  const image = await loadImageFromFile(file);
  const attempts = [
    { maxDimension, quality },
    { maxDimension: Math.min(maxDimension, 2000), quality: Math.min(quality, 0.86) },
    { maxDimension: Math.min(maxDimension, 1600), quality: Math.min(quality, 0.82) },
    { maxDimension: Math.min(maxDimension, 1280), quality: Math.min(quality, 0.78) },
    { maxDimension: Math.min(maxDimension, 1024), quality: Math.min(quality, 0.74) },
  ];
  let lastOptimizedFile = file;

  for (const attempt of attempts) {
    const { width, height } = getImageDimensions(image, attempt.maxDimension);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      throw createUserVisibleError('Unable to prepare image for upload. Please choose another image.');
    }

    context.drawImage(image, 0, 0, width, height);

    const blob = await canvasToBlob(canvas, 'image/jpeg', attempt.quality);
    const fileName = (file.name || 'meme-image').replace(/\.[^.]+$/, '') || 'meme-image';
    lastOptimizedFile = new File([blob], `${fileName}.jpg`, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });

    if (!maxBytes || lastOptimizedFile.size <= maxBytes) {
      return lastOptimizedFile;
    }
  }

  return lastOptimizedFile;
};

const prepareSelectedMemeImage = async (file) => {
  const sourceWasHeic = isHeicFile(file);
  let imageFile = sourceWasHeic ? await convertHeicToJpeg(file) : file;

  if (sourceWasHeic || imageFile.size > MAX_MEME_UPLOAD_BYTES) {
    imageFile = await optimizeMemeImageFile(imageFile, {
      maxDimension: MEME_EDITOR_MAX_DIMENSION,
      quality: MEME_EDITOR_IMAGE_QUALITY,
    });
  }

  if (imageFile.size > MAX_MEME_UPLOAD_BYTES) {
    throw createUserVisibleError(MEME_IMAGE_TOO_LARGE_MESSAGE);
  }

  return imageFile;
};

const prepareMemeUploadImage = async (file) => {
  const optimizedFile = await optimizeMemeImageFile(file, {
    maxDimension: MAX_MEME_IMAGE_DIMENSION,
    quality: MEME_IMAGE_QUALITY,
  });

  if (optimizedFile.size > MAX_MEME_UPLOAD_BYTES) {
    throw createUserVisibleError(MEME_IMAGE_TOO_LARGE_MESSAGE);
  }

  return optimizedFile;
};

const createSquareMemeImageFile = async (
  file,
  {
    maxDimension = MEME_EDITOR_MAX_DIMENSION,
    quality = MEME_EDITOR_IMAGE_QUALITY,
  } = {}
) => {
  if (!file || typeof window === 'undefined' || typeof document === 'undefined') {
    return file;
  }

  const image = await loadImageFromFile(file);
  const sourceWidth = image.naturalWidth || image.width || 1;
  const sourceHeight = image.naturalHeight || image.height || 1;
  const side = Math.max(1, Math.min(maxDimension, Math.max(sourceWidth, sourceHeight)));
  const scale = Math.min(side / sourceWidth, side / sourceHeight);
  const drawWidth = Math.max(1, Math.round(sourceWidth * scale));
  const drawHeight = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = side;
  canvas.height = side;

  const context = canvas.getContext('2d');
  if (!context) {
    throw createUserVisibleError('Unable to prepare image for upload. Please choose another image.');
  }

  context.fillStyle = '#050607';
  context.fillRect(0, 0, side, side);
  context.drawImage(
    image,
    Math.round((side - drawWidth) / 2),
    Math.round((side - drawHeight) / 2),
    drawWidth,
    drawHeight
  );

  const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
  return new File([blob], getJpegFileName(file.name || 'meme-image'), {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
};

const getMemeUploadUrls = () => {
  const sameOriginUploadUrl = `${server}/meme/create-meme`;
  const directBackendUploadUrl = `${backend_url.replace(/\/+$/, '')}/api/v2/meme/create-meme`;

  if (sameOriginUploadUrl === directBackendUploadUrl || !sameOriginUploadUrl.startsWith('/')) {
    return [sameOriginUploadUrl];
  }

  return [sameOriginUploadUrl, directBackendUploadUrl];
};

const postMemeFormData = async (formData) => {
  const uploadUrls = getMemeUploadUrls();
  let lastError;

  for (const uploadUrl of uploadUrls) {
    try {
      return await axios.post(uploadUrl, formData, {
        withCredentials: true,
        timeout: 120000,
      });
    } catch (error) {
      lastError = error;

      if (error?.message !== 'Network Error' || uploadUrl === uploadUrls[uploadUrls.length - 1]) {
        throw error;
      }
    }
  }

  throw lastError;
};

function MemeCreator({ ownerId, profileType, onClose }) {
  const [text, setText] = useState('');
  const [textVisible, setTextVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showImageCropper, setShowImageCropper] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isPreparingImage, setIsPreparingImage] = useState(false);
  const [error, setError] = useState(null);

  // State variables for styling
  const [fontSize, setFontSize] = useState(40);
  const [fontColor, setFontColor] = useState('#ffffff');
  const [fontFamily, setFontFamily] = useState('Impact');
  const [textPosition, setTextPosition] = useState({ x: 0, y: 0 });
  const [textSize, setTextSize] = useState({ width: 200, height: 100 });
  const [previewSize, setPreviewSize] = useState({ width: 0, height: 0 });

  // New state variables for initial sizes
  const [initialTextSize, setInitialTextSize] = useState({ width: 200, height: 100 });
  const [initialFontSize, setInitialFontSize] = useState(fontSize);

  // References for the text div and image
  const textRef = useRef(null);
  const imageRef = useRef(null);

  const getRenderedImageSize = useCallback(() => {
    const image = imageRef.current;

    return {
      width: image?.clientWidth || image?.offsetWidth || 0,
      height: image?.clientHeight || image?.offsetHeight || 0,
    };
  }, []);

  const clampTextBoxToPreview = useCallback(() => {
    const { width: imageWidth, height: imageHeight } = getRenderedImageSize();

    if (!imageWidth || !imageHeight) {
      return;
    }

    setPreviewSize({ width: imageWidth, height: imageHeight });
    setTextSize((currentSize) => {
      const nextSize = {
        width: clampDimension(currentSize.width, MIN_MEME_TEXT_BOX_WIDTH, imageWidth),
        height: clampDimension(currentSize.height, MIN_MEME_TEXT_BOX_HEIGHT, imageHeight),
      };

      setTextPosition((currentPosition) => ({
        x: clampNumber(currentPosition.x, 0, Math.max(0, imageWidth - nextSize.width)),
        y: clampNumber(currentPosition.y, 0, Math.max(0, imageHeight - nextSize.height)),
      }));

      return nextSize;
    });
  }, [getRenderedImageSize]);

  useEffect(() => {
    if (!previewUrl) {
      return undefined;
    }

    clampTextBoxToPreview();
    window.addEventListener('resize', clampTextBoxToPreview);

    return () => {
      window.removeEventListener('resize', clampTextBoxToPreview);
    };
  }, [clampTextBoxToPreview, previewUrl]);

  useEffect(() => {
    if (textRef.current) {
      const { offsetHeight } = textRef.current;
      setTextSize((prevSize) => ({
        ...prevSize,
        height: previewSize.height
          ? clampDimension(offsetHeight, MIN_MEME_TEXT_BOX_HEIGHT, previewSize.height)
          : offsetHeight,
      }));
      setTextPosition((prevPosition) => ({
        x: previewSize.width
          ? clampNumber(prevPosition.x, 0, Math.max(0, previewSize.width - textSize.width))
          : prevPosition.x,
        y: previewSize.height
          ? clampNumber(prevPosition.y, 0, Math.max(0, previewSize.height - offsetHeight))
          : prevPosition.y,
      }));
    }
  }, [fontFamily, fontSize, previewSize.height, previewSize.width, text, textSize.width]);

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    setError(null);
    setIsPreparingImage(true);

    try {
      const imageFile = await prepareSelectedMemeImage(file);
      setSelectedFile(imageFile);
      setShowImageCropper(true);
    } catch (error) {
      console.error('Error preparing image:', error);
      const message = error?.isUserVisible
        ? error.message
        : 'That image could not be prepared. Please choose a JPEG, PNG, WEBP, HEIC, or HEIF image.';
      setSelectedFile(null);
      setCroppedImage(null);
      setPreviewUrl(null);
      setShowImageCropper(false);
      setError(message);
      toast.error(message);
    } finally {
      setIsPreparingImage(false);
    }
  };

  const updateImage = (croppedFile) => {
    setCroppedImage(croppedFile);
    const previewUrl = URL.createObjectURL(croppedFile);
    setPreviewUrl(previewUrl);
    setPreviewSize({ width: 0, height: 0 });
    setTextPosition({ x: 0, y: 0 });
    setText('');
    setTextVisible(false);
  };

  const handleAddText = () => {
    const { width: imageWidth, height: imageHeight } = getRenderedImageSize();
    const nextWidth = imageWidth ? Math.min(Math.max(imageWidth * 0.64, MIN_MEME_TEXT_BOX_WIDTH), imageWidth) : textSize.width;
    const nextHeight = imageHeight ? Math.min(Math.max(imageHeight * 0.18, MIN_MEME_TEXT_BOX_HEIGHT), imageHeight) : textSize.height;

    setText((currentText) => currentText.trim() || 'Your Text Here');
    setTextSize({ width: nextWidth, height: nextHeight });
    setTextPosition({
      x: imageWidth ? Math.max(0, (imageWidth - nextWidth) / 2) : 0,
      y: imageHeight ? Math.max(0, (imageHeight - nextHeight) / 2) : 0,
    });
    setTextVisible(true);
  };

  const handleRemoveText = () => {
    setText('');
    setTextVisible(false);
  };

  const handleTextInput = () => {
    if (textRef.current) {
      const selection = window.getSelection();
      const caretPosition = selection?.rangeCount ? selection.getRangeAt(0).startOffset : null;
      setText(textRef.current.innerText);

      // Restore caret position
      setTimeout(() => {
        const textNode = textRef.current?.childNodes?.[0];
        if (!textNode || caretPosition === null) {
          return;
        }

        const range = document.createRange();
        const selection = window.getSelection();
        range.setStart(textNode, Math.min(caretPosition, textNode.textContent.length));
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }, 0);
    }
  };

  const handleSubmit = async () => {
    if (!croppedImage) {
      setError('Please upload an image.');
      return;
    }

    if (!ownerId || !profileType) {
      setError('This page is missing profile details. Please refresh and try again.');
      return;
    }

    const trimmedText = textVisible ? text.trim() : '';
    const hasText = trimmedText.length > 0;

    // Get image dimensions
    const imageWidth = imageRef.current ? imageRef.current.offsetWidth : 1;
    const imageHeight = imageRef.current ? imageRef.current.offsetHeight : 1;

    // Calculate percentages
    const textPositionXPercent = hasText ? (textPosition.x / imageWidth) * 100 : 0;
    const textPositionYPercent = hasText ? (textPosition.y / imageHeight) * 100 : 0;
    const textWidthPercent = hasText ? (textSize.width / imageWidth) * 100 : 0;
    const textHeightPercent = hasText ? (textSize.height / imageHeight) * 100 : 0;
    const effectiveFontSize = previewSize.width
      ? clampNumber(fontSize, 12, Math.max(18, previewSize.width * 0.18))
      : fontSize;
    const fontSizePercent = hasText ? (effectiveFontSize / imageWidth) * 100 : 0;

    setLoading(true);
    setError(null);

    try {
      const squareMemeImage = await createSquareMemeImageFile(croppedImage);
      const uploadImage = await prepareMemeUploadImage(squareMemeImage);
      const formData = new FormData();
      formData.append('image', uploadImage);
      formData.append('ownerId', String(ownerId));
      formData.append('profileType', profileType);
      formData.append('overlayText', trimmedText);
      formData.append('fontSize', fontSizePercent);
      formData.append('fontColor', fontColor);
      formData.append('fontFamily', fontFamily);
      formData.append('textPositionX', textPositionXPercent);
      formData.append('textPositionY', textPositionYPercent);
      formData.append('textWidth', textWidthPercent);
      formData.append('textHeight', textHeightPercent);

      const response = await postMemeFormData(formData);
      console.log('Meme created:', response.data);
      toast.success('Meme created successfully!');
      setShowImageCropper(false);
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error creating meme:', error);
      const message = getMemeErrorMessage(error);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const previewFontSize = previewSize.width
    ? clampNumber(fontSize, 12, Math.max(18, previewSize.width * 0.18))
    : fontSize;

  return (
    <div className="meme-creator">
       <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
      <h2>Create Your Meme</h2>
      {error && <div className="meme-error-message">{error}</div>}
      <div className="meme-upload-section">
        <label htmlFor="image-upload" className="meme-upload-label">
          <FontAwesomeIcon icon={faUpload} /> {isPreparingImage ? 'Preparing Image...' : 'Upload Image'}
        </label>
        <input
          id="image-upload"
          type="file"
          accept="image/*,.heic,.heif,image/heic,image/heif"
          disabled={isPreparingImage || loading}
          onChange={handleImageUpload}
          style={{ display: 'none' }}
        />
      </div>

      {previewUrl && !textVisible && (
        <div className="meme-text-toolbar">
          <button
            type="button"
            className="meme-add-text-button"
            onClick={handleAddText}
            disabled={loading || isPreparingImage}
          >
            Add Text
          </button>
        </div>
      )}

      {/* Styling Controls */}
      {textVisible && <div className="meme-styling-controls">
        <label>
          Font Color:
          <input
            type="color"
            value={fontColor}
            onChange={(e) => setFontColor(e.target.value)}
          />
        </label>
        <label>
          Font Family:
          <select
            value={fontFamily}
            onChange={(e) => setFontFamily(e.target.value)}
          >
            <option value="Impact">Impact</option>
            <option value="Arial">Arial</option>
            <option value="Comic Sans MS">Comic Sans</option>
            <option value="Courier New">Courier New</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Georgia">Georgia</option>
            <option value="Verdana">Verdana</option>
            <option value="Tahoma">Tahoma</option>
            <option value="Trebuchet MS">Trebuchet MS</option>
            <option value="Lucida Console">Lucida Console</option>
            <option value="Gill Sans">Gill Sans</option>
            <option value="Roboto">Roboto</option>
          </select>
        </label>
      </div>}

      {previewUrl && (
        <div className="meme-preview">
          <div className="meme-preview-stage">
            <img
              ref={imageRef}
              src={previewUrl}
              alt="Meme Preview"
              className="meme-preview-image"
              onLoad={clampTextBoxToPreview}
            />

          <div className="meme-preview-text-layer">
            {textVisible && (
            <Rnd
              size={{ width: textSize.width, height: textSize.height }}
              position={{ x: textPosition.x, y: textPosition.y }}
              onDragStop={(e, d) => {
                const { width: imageWidth, height: imageHeight } = getRenderedImageSize();
                setTextPosition({
                  x: clampNumber(d.x, 0, Math.max(0, imageWidth - textSize.width)),
                  y: clampNumber(d.y, 0, Math.max(0, imageHeight - textSize.height)),
                });
              }}
              onResizeStart={(e, dir, ref) => {
                setInitialTextSize({
                  width: parseInt(ref.style.width),
                  height: parseInt(ref.style.height),
                });
                setInitialFontSize(fontSize);
              }}
              onResize={(e, direction, ref, delta, position) => {
                const { width: imageWidth, height: imageHeight } = getRenderedImageSize();
                const newWidth = clampDimension(parseInt(ref.style.width), MIN_MEME_TEXT_BOX_WIDTH, imageWidth);
                const newHeight = clampDimension(parseInt(ref.style.height), MIN_MEME_TEXT_BOX_HEIGHT, imageHeight);
                const nextPosition = {
                  x: clampNumber(position.x, 0, Math.max(0, imageWidth - newWidth)),
                  y: clampNumber(position.y, 0, Math.max(0, imageHeight - newHeight)),
                };

                // Calculate the area ratio
                const areaRatio =
                  (newWidth * newHeight) /
                  (initialTextSize.width * initialTextSize.height);

                // Adjust font size based on area ratio
                const adjustedFontSize = initialFontSize * Math.sqrt(areaRatio);

                setFontSize(adjustedFontSize);
                setTextSize({
                  width: newWidth,
                  height: newHeight,
                });
                setTextPosition(nextPosition);
              }}
              bounds="parent"
              minWidth={Math.min(MIN_MEME_TEXT_BOX_WIDTH, previewSize.width || MIN_MEME_TEXT_BOX_WIDTH)}
              minHeight={Math.min(MIN_MEME_TEXT_BOX_HEIGHT, previewSize.height || MIN_MEME_TEXT_BOX_HEIGHT)}
              maxWidth={previewSize.width || undefined}
              maxHeight={previewSize.height || undefined}
              cancel=".meme-preview-remove-text-button"
              enableResizing={{
                top: true,
                right: true,
                bottom: true,
                left: true,
                topRight: true,
                bottomRight: true,
                bottomLeft: true,
                topLeft: true,
              }}
              style={{
                boxSizing: 'border-box',
                padding: 0,
                margin: 0,
              }}
            >
              <button
                type="button"
                className="meme-preview-remove-text-button"
                aria-label="Remove meme text"
                onClick={handleRemoveText}
              >
                X
              </button>
              <div
                ref={textRef}
                className="meme-preview-text-box"
                contentEditable
                suppressContentEditableWarning
                onInput={handleTextInput}
                style={{
                  fontSize: `${previewFontSize}px`,
                  color: fontColor,
                  fontFamily: fontFamily,
                  lineHeight: '1',
                  textShadow: '0 2px 6px rgba(0, 0, 0, 0.82), 0 0 2px rgba(0, 0, 0, 0.9)',
                }}
              >
                {text}
              </div>
            </Rnd>
            )}
            </div>
          </div>
        </div>
      )}
      <div className="meme-creator-actions">
        <button
          type="button"
          onClick={onClose}
          disabled={loading || isPreparingImage}
          className="meme-cancel-button"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading || isPreparingImage}
          className="meme-submit-button"
        >
          {loading ? 'Creating...' : 'Create Meme'}
        </button>
      </div>

      {/* Image Cropper Modal */}
      {showImageCropper && (
        <ImageCropperModal
          updateAvatar={updateImage}
          closeModal={() => setShowImageCropper(false)}
          initialImage={selectedFile}
          aspectRatio={1}
          minWidth={200}
          isAvatar={false}
          isPost={false}
          isLogo={false}
        />
      )}
    </div>
  );
}

export default MemeCreator;
